import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";

const asArray = (value) => Array.isArray(value) ? value : [];

const normalizePreferences = (responseData) => {
  const payload = responseData?.preferences
    || responseData?.data?.preferences
    || responseData?.data
    || responseData
    || {};

  const activities = asArray(payload.activity_preferences?.preferred_activities
    || payload.preferred_activities
    || []).map((activity) => ({
    ...activity,
    id: activity.id ?? activity.activityid,
    name: activity.name ?? activity.activityname,
  }));
  const workgroups = asArray(payload.workgroup_preferences?.preferred_workgroups
    || payload.preferred_workgroups
    || []).map((workgroup) => ({
    ...workgroup,
    id: workgroup.id ?? workgroup.workgroupid,
    name: workgroup.name ?? workgroup.workgroupname,
  }));
  const skills = asArray(payload.skills).map((skill) => ({
    ...skill,
    id: skill.id ?? skill.skillid,
    name: skill.name ?? skill.skillname,
    level: skill.level ?? skill.skilllevel,
  }));
  const interests = asArray(payload.interest_skills).map((skill) => ({
    ...skill,
    id: skill.id ?? skill.skillid,
    name: skill.name ?? skill.skillname,
  }));
  const availability = asArray(payload.availability).length > 0
    ? payload.availability
    : asArray(payload.recurrences).map((recurrence) => {
      const translation = recurrence.recurrence_translation || {};
      const dates = [translation.starting_on, translation.ending_on].filter(Boolean).join(" to ");
      const detail = [recurrence.availability_status, dates, recurrence.recurrence_note]
        .filter(Boolean)
        .join(" - ");
      return detail || "Availability rule configured.";
    });
  const rules = asArray(payload.scheduling_rules).length > 0
    ? payload.scheduling_rules
    : asArray(payload.availability?.rules);
  const normalized = {
    ...payload,
    activity_preferences: {
      ...(payload.activity_preferences || {}),
      preferred_activities: activities,
    },
    workgroup_preferences: {
      ...(payload.workgroup_preferences || {}),
      preferred_workgroups: workgroups,
    },
    skills,
    interest_skills: interests,
    availability,
    scheduling_rules: rules,
  };

  const activityNames = activities.map((activity) => activity.name || activity.activityname).filter(Boolean);
  const workgroupNames = workgroups.map((workgroup) => workgroup.name || workgroup.workgroupname).filter(Boolean);
  const sentences = [
    skills.length
      ? `Has ${skills.length} assigned skill${skills.length === 1 ? "" : "s"}: ${skills.map((skill) => skill.name).filter(Boolean).join(", ")}.`
      : "Has no assigned skills.",
    interests.length
      ? `Is interested in learning or serving with: ${interests.map((skill) => skill.name).filter(Boolean).join(", ")}.`
      : "Has no additional skill interests recorded.",
    normalized.activity_preferences.interested_in_any_activity
      ? "Is interested in any activity."
      : activityNames.length
        ? `Prefers these activities: ${activityNames.join(", ")}.`
        : "Has no preferred activities recorded.",
    normalized.workgroup_preferences.interested_in_any_workgroup
      ? "Is interested in any eligible workgroup."
      : workgroupNames.length
        ? `Prefers these workgroups: ${workgroupNames.join(", ")}.`
        : "Has no preferred workgroups recorded.",
    availability.length
      ? `Availability is configured as follows: ${availability.join("; ")}.`
      : "Has no availability rules recorded.",
    rules.length
      ? `Scheduling rules include: ${rules.map((rule) => rule.description || rule.rule_description || rule.label).filter(Boolean).join("; ")}.`
      : "Has no scheduling rules recorded.",
  ];

  return { ...normalized, summary: sentences };
};

export default {
  state: {
    personPreferences: null,
    loadingPreferences: false,
  },
  mutations: {
    setPersonPreferences(state, payload) {
      state.personPreferences = payload;
    },
    setLoadingPreferences(state, payload) {
      state.loadingPreferences = payload;
    },
  },
  actions: {
    async fetchPersonPreferences({ commit }, { personId, locationId }) {
      commit("setLoadingPreferences", true);
      try {
        const response = await api.post("/personajax", {
          cmd: "getpersonpreferencesjson",
          personid: personId,
          locationid: locationId,
        }); 

        const preferences = normalizePreferences(response.data);
        commit("setPersonPreferences", preferences);
        return { preferences };
      } catch (error) {
        handleApiError(error, "Failed to fetch person preferences");
        throw error;
      } finally {
        commit("setLoadingPreferences", false);
      }
    },
  },
  getters: {
    getPersonPreferences: (state) => state.personPreferences,
    isPersonPreferencesLoading: (state) => state.loadingPreferences,
  },
};
